import { Children, useState, type ReactNode } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Scrollbar } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  SLIDER_EDGE_INNER,
  SLIDER_EDGE_OUTER,
  SLIDER_NAV_ICON_STROKE,
  sliderNavIconClass,
  sliderNavNextClass,
  sliderNavPrevClass,
  sliderToolbarNavButtonClass,
  sliderToolbarNavGroupClass,
} from '@/lib/sliderNavStyles'
import { cn } from '@/lib/utils'

export type CardSliderSize = 'xs' | 'sm' | 'md' | 'city' | 'category' | 'property' | 'blog' | 'testimonial' | 'video' | 'ebook' | 'ebook-half' | 'product-half'

const SLIDE_WIDTH: Record<CardSliderSize, string> = {
  xs: '!w-[150px] sm:!w-[160px]',
  sm: '!w-[190px] sm:!w-[210px]',
  md: '!w-[220px] sm:!w-[240px]',
  city: '!w-[220px] sm:!w-[280px] md:!w-[320px]',
  category: 'max-md:!w-[200px] md:!w-[248px] lg:!w-[268px]',
  property: 'max-md:!w-[220px] md:!w-[288px] lg:!w-[311px]',
  blog: 'max-md:!w-[240px] md:!w-[268px] lg:!w-[300px]',
  testimonial: '!w-[300px] sm:!w-[340px]',
  video: '!w-[280px] sm:!w-[320px]',
  ebook: 'max-md:!w-[148px] sm:!w-[165px] md:!w-[190px] lg:!w-[210px]',
  'ebook-half': 'max-md:!w-[138px] sm:!w-[158px] md:!w-[172px] lg:!w-[182px]',
  'product-half': 'max-md:!w-[178px] sm:!w-[192px] md:!w-[208px] lg:!w-[222px]',
}

interface CardSliderProps {
  children: ReactNode
  size?: CardSliderSize
  spaceBetween?: number
  className?: string
  /** side = prev/next on image left/right edges (legacy qbcon.in style) */
  navVariant?: 'header' | 'side'
  hideScrollbar?: boolean
  /** Align first slide with section heading (no left edge inset for nav) */
  flushStart?: boolean
  /** Stretch all slides to the tallest card height */
  equalHeight?: boolean
}

export function CardSlider({
  children,
  size = 'sm',
  spaceBetween = 8,
  className,
  navVariant = 'side',
  hideScrollbar = false,
  flushStart,
  equalHeight = false,
}: CardSliderProps) {
  const alignedStart = flushStart ?? navVariant === 'side'
  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  const slides = Children.toArray(children)
  const showNav = slides.length > 1

  const updateEdges = (s: SwiperType) => {
    setAtStart(s.isBeginning)
    setAtEnd(s.isEnd)
  }

  const navIcon = sliderNavIconClass('md')
  const toolbarBtn = sliderToolbarNavButtonClass('sm')

  const headerNav = showNav ? (
    <div className="mb-2.5 flex items-center justify-end sm:mb-3">
      <div className={sliderToolbarNavGroupClass()}>
        <button
          type="button"
          aria-label="Previous slide"
          disabled={atStart}
          onClick={() => swiper?.slidePrev()}
          className={toolbarBtn}
        >
          <ChevronLeft
            className={sliderNavIconClass('sm', atStart)}
            strokeWidth={SLIDER_NAV_ICON_STROKE}
            aria-hidden
          />
        </button>
        <span className="h-4 w-px shrink-0 bg-neutral-200/90" aria-hidden />
        <button
          type="button"
          aria-label="Next slide"
          disabled={atEnd}
          onClick={() => swiper?.slideNext()}
          className={toolbarBtn}
        >
          <ChevronRight
            className={sliderNavIconClass('sm', atEnd)}
            strokeWidth={SLIDER_NAV_ICON_STROKE}
            aria-hidden
          />
        </button>
      </div>
    </div>
  ) : null

  const track = (
    <div className="overflow-hidden">
      <Swiper
        modules={hideScrollbar ? [FreeMode] : [FreeMode, Scrollbar]}
        spaceBetween={spaceBetween}
        slidesPerView="auto"
        freeMode={{ enabled: true, momentum: true }}
        scrollbar={hideScrollbar ? undefined : { draggable: true, hide: false }}
        onSwiper={(s) => {
          setSwiper(s)
          updateEdges(s)
          requestAnimationFrame(() => updateEdges(s))
        }}
        onSlideChange={updateEdges}
        onResize={updateEdges}
        onReachBeginning={() => setAtStart(true)}
        onReachEnd={() => setAtEnd(true)}
        onFromEdge={updateEdges}
        className={cn(
          'card-slider-swiper overflow-hidden',
          equalHeight && 'card-slider-equal-height',
          alignedStart ? 'pl-0!' : 'px-0.5!',
          !hideScrollbar && 'pb-3!',
        )}
      >
        {slides.map((slide, i) => (
          <SwiperSlide
            key={i}
            className={cn(
              equalHeight ? '!h-auto !self-stretch' : 'h-auto!',
              SLIDE_WIDTH[size],
            )}
          >
            {equalHeight ? <div className="h-full">{slide}</div> : slide}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )

  return (
    <div className={cn('card-slider min-w-0', className)}>
      {navVariant === 'header' && (
        <div className="flex h-full min-h-0 flex-col">
          {headerNav}
          <div className="min-h-0 flex-1">{track}</div>
        </div>
      )}

      {navVariant === 'side' && (
        <div className={alignedStart ? 'overflow-visible pl-0 pr-0 md:pr-[22px]' : SLIDER_EDGE_OUTER}>
          <div className={SLIDER_EDGE_INNER}>
            {track}
            {showNav && (
              <>
                <button
                  type="button"
                  aria-label="Previous slide"
                  disabled={atStart}
                  onClick={() => swiper?.slidePrev()}
                  className={cn(sliderNavPrevClass('md', 'image'), 'hidden md:flex')}
                >
                  <ChevronLeft className={navIcon} strokeWidth={SLIDER_NAV_ICON_STROKE} />
                </button>
                <button
                  type="button"
                  aria-label="Next slide"
                  disabled={atEnd}
                  onClick={() => swiper?.slideNext()}
                  className={cn(sliderNavNextClass('md', 'image'), 'hidden md:flex')}
                >
                  <ChevronRight className={navIcon} strokeWidth={SLIDER_NAV_ICON_STROKE} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
